import React, { useEffect, useState } from 'react';
import { apiHandler } from '../../../api/apihandler';
import { INFLUENCERS_LIST, USER_FOLLOW_REQUEST } from '../../../api/constants';
import { apiSucceeded, getAvatarColor, getInitials } from '../feed/feedUtils';
import MyStatsWidget from '../../../components/nemonto/MyStatsWidget';
import PromoCard from '../../../components/nemonto/PromoCard';
import EmptyState from '../../../components/nemonto/EmptyState';
import './social-list-page.css';

const PersonAvatar = ({ photo, name }) => {
  if (photo) return <img src={photo} alt={name} className="nmsl-avatar" />;
  return (
    <div className="nmsl-avatar" style={{ background: getAvatarColor(name || 'User') }}>
      {getInitials(...(name || 'User').split(' '))}
    </div>
  );
};

const InfluencerListPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());

  async function loadPage(pageNo, replace) {
    setLoading(true);
    try {
      const res = await apiHandler(INFLUENCERS_LIST, 'POST', { page: pageNo, limit: 20 });
      if (apiSucceeded(res)) {
        const list = res?.data?.data?.users?.data || [];
        const meta = res?.data?.data?.users?.meta;
        setUsers((prev) => (replace ? list : [...prev, ...list]));
        setHasMore(!!meta && meta.current_page < meta.last_page);
        setPage(pageNo);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadPage(1, true); }, []);

  async function toggleFollow(userId, index, currentlyFollowing) {
    if (busyIds.has(userId)) return;
    setBusyIds((prev) => new Set([...prev, userId]));
    try {
      const res = await apiHandler(USER_FOLLOW_REQUEST, 'POST', { follower_id: userId });
      if (apiSucceeded(res)) {
        setUsers((prev) => {
          const copy = [...prev];
          copy[index] = { ...copy[index], is_following: currentlyFollowing ? 0 : 1 };
          return copy;
        });
      }
    } catch (e) { console.error(e); }
    finally {
      setBusyIds((prev) => { const next = new Set(prev); next.delete(userId); return next; });
    }
  }

  return (
    <div className="nmsl-layout">
      <div className="nmsl-main">
        <div className="nmsl-page-header">
          <div className="nmsl-header">
            <h3 className="nmsl-title">Influencers</h3>
            {users.length > 0 && <span className="nmsl-count-pill">{users.length}</span>}
          </div>
          <p className="nmsl-subtitle">Discover top creators and influencers to follow.</p>
        </div>

        <div className="nmsl-main-body">
          {loading && users.length === 0 && <p className="nmsl-loading">Loading…</p>}
          {!loading && users.length === 0 && (
            <EmptyState icon="star" title="No influencers right now" sub="Check back soon for more creators to follow." />
          )}

          {users.length > 0 && (
            <div className="nmsl-grid">
              {users.map((u, index) => {
                const name = `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || 'User';
                const isFollowing = u?.is_following === 1;
                return (
                  <div key={u.id} className="nmsl-card">
                    <PersonAvatar photo={u?.user_setting?.photo} name={name} />
                    <div className="nmsl-body">
                      <div className="nmsl-name">{name}</div>
                      <div className="nmsl-sub">{u?.follower_count ?? 0} followers</div>
                    </div>
                    <button
                      type="button"
                      className={`nmsl-follow-btn ${isFollowing ? 'nmsl-following' : ''}`}
                      disabled={busyIds.has(u.id)}
                      onClick={() => toggleFollow(u.id, index, isFollowing)}
                    >
                      {isFollowing ? 'Following' : 'Follow'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {hasMore && (
            <div className="nmsl-load-more-wrap">
              <button type="button" className="nmsl-load-more-btn" onClick={() => loadPage(page + 1, false)} disabled={loading}>
                {loading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </div>

      <aside className="nmsl-rail">
        <PromoCard
          label="Made it"
          title="See what everyone's cooking"
          sub="Browse real attempts from the community and get inspired for your next dish."
          ctaText="Explore Made It"
          to="/dashboard/app/profile"
          state={{ tab: 'madeit' }}
        />
        <MyStatsWidget />
      </aside>
    </div>
  );
};

export default InfluencerListPage;
